import { Token, YStack, YStackProps, getTokenValue } from "tamagui";

export interface GradientCardThemeProps {
  error?: boolean;
  info?: boolean;
  success?: boolean;
  warning?: boolean;
  neutral?: boolean;
  isSelected?: boolean;
}

export type GradientCardProps = YStackProps & GradientCardThemeProps;

export const GradientCard = ({
  error,
  info,
  success,
  warning,
  neutral,
  isSelected,
  children,
  ...rest
}: GradientCardProps) => {
  const { backgroundStart, backgroundEnd, selectedBorderColor } =
    getThemeTokens({
      error,
      info,
      success,
      warning,
      neutral,
    });
  const gradientStartColor = getTokenValue(backgroundStart, "color");
  const gradientEndColor = getTokenValue(backgroundEnd, "color");
  const backgroundColor = gradientStartColor;
  const backgroundImage = `radial-gradient(120% 140% at 20% 10%, ${gradientStartColor} 30%, ${gradientEndColor} 100%)`;

  return (
    <YStack
      gap="$3.5"
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      backgroundOrigin="border-box"
      padding="$3.5"
      borderWidth={2}
      borderColor={isSelected ? selectedBorderColor : "$white5"}
      borderRadius="$6"
      {...rest}
    >
      {children}
    </YStack>
  );
};

const getThemeTokens = ({
  error,
  info,
  success,
  warning,
  neutral,
}: GradientCardThemeProps): {
  backgroundStart: Token;
  backgroundEnd: Token;
  selectedBorderColor: Token;
} => {
  if (error) {
    return {
      backgroundStart: "$gradientErrorStart" as Token,
      backgroundEnd: "$gradientErrorEnd" as Token,
      selectedBorderColor: "$error" as Token,
    };
  }

  if (info) {
    return {
      backgroundStart: "$gradientInfoStart" as Token,
      backgroundEnd: "$gradientInfoEnd" as Token,
      selectedBorderColor: "$info" as Token,
    };
  }

  if (success) {
    return {
      backgroundStart: "$gradientSuccessStart" as Token,
      backgroundEnd: "$gradientSuccessEnd" as Token,
      selectedBorderColor: "$successGreen" as Token,
    };
  }

  if (warning) {
    return {
      backgroundStart: "$gradientPoorStart" as Token,
      backgroundEnd: "$gradientPoorEnd" as Token,
      selectedBorderColor: "$warning" as Token,
    };
  }

  if (neutral) {
    return {
      backgroundStart: "$gradientNeutralStart" as Token,
      backgroundEnd: "$gradientNeutralEnd" as Token,
      selectedBorderColor: "$neutral" as Token,
    };
  }

  return {
    backgroundStart: "$gradientGoodStart" as Token,
    backgroundEnd: "$gradientGoodEnd" as Token,
    selectedBorderColor: "$transparentBorder" as Token,
  };
};
